/**
 * Commitment lanes for Plans home + Engine scenario chrome.
 *
 * Authorized = Plan of Record (not a scenario).
 * Candidate  = working model being shaped (Engine scenario).
 * Hypothesis = forked what-if against a candidate or PoR.
 */

import type { Scenario } from './planningEngineModule.types';

export type ScenarioLane = 'candidate' | 'hypothesis';

export type CommitmentLane = 'authorized' | ScenarioLane;

const HYPOTHESIS_NAME =
  /(?:^|\s)(?:delay\s*\d+\s*wk|what-?if|hypothesis|counterfactual)|—\s*delay\b/i;

/** Derive lane for an Engine scenario (PoR is never a scenario). */
export function deriveScenarioLane(scenario: Pick<Scenario, 'name' | 'notes'>): ScenarioLane {
  const blob = `${scenario.name}\n${scenario.notes ?? ''}`;
  if (HYPOTHESIS_NAME.test(blob)) return 'hypothesis';
  return 'candidate';
}

export const SCENARIO_LANE_LABEL: Record<ScenarioLane, string> = {
  candidate: 'Candidate',
  hypothesis: 'Hypothesis',
};

export const COMMITMENT_LANE_LABEL: Record<CommitmentLane, string> = {
  authorized: 'Authorized',
  candidate: 'Candidate',
  hypothesis: 'Hypothesis',
};

/** Badge classes — product shell (light + dark tokens). */
export const SCENARIO_LANE_BADGE_CLASS: Record<ScenarioLane, string> = {
  candidate:
    'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)]',
  hypothesis:
    'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)] dark:border-[var(--mustard)]',
};

export const AUTHORIZED_LANE_BADGE_CLASS =
  'bg-[var(--green-dim)] text-[var(--green)] border-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)] dark:border-[var(--green)]';

/** Option label for <select> (no custom option styling). */
export function scenarioPickerLabel(scenario: Scenario): string {
  const lane = deriveScenarioLane(scenario);
  const mark = scenario.active ? ' ✓' : '';
  return `${SCENARIO_LANE_LABEL[lane]} · ${scenario.name}${mark}`;
}

export function partitionScenariosByLane(scenarios: Scenario[]): {
  candidates: Scenario[];
  hypotheses: Scenario[];
} {
  const candidates: Scenario[] = [];
  const hypotheses: Scenario[] = [];
  for (const s of scenarios) {
    if (deriveScenarioLane(s) === 'hypothesis') hypotheses.push(s);
    else candidates.push(s);
  }
  return { candidates, hypotheses };
}
